var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a, _b, _c;
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useCallout, useOkapiKy } from "@folio/stripes/core";
import { PersonLegalFormEnum } from "../types/Api";
import { ErrorType } from "../types/ErrorType";
import { assignNaturalPersonAuthorityId, fetchNaturalPersonById, fetchNaturalPersonMergeCandidates, mergeNaturalPersonDuplicates, mergeNaturalPersonInto, proposeNaturalPersonMerge, sendNaturalPersonToAuthority, } from "../api/naturalPersonService";
import { assignLegalPersonAuthorityId, fetchLegalPersonById, fetchLegalPersonMergeCandidates, mergeLegalPersonDuplicates, mergeLegalPersonInto, proposeLegalPersonMerge, sendLegalPersonToAuthority, } from "../api/legalPersonService";
import { canMergePerson, canSendPerson, isPersonUnlinked, parsePersonAuthorityLinkError, } from "../utils/personAuthorityLink";
import { usePermissions } from "./usePermissions";
import { useAuthoritySendErrorCallout } from "./useAuthoritySendErrorCallout";
import { PERSON_TITLES_QUERY_KEY } from "./usePersonTitles";
var KIND_CONFIG = {
    natural: {
        legalForm: PersonLegalFormEnum.NATURAL,
        listQueryKey: "natural-persons",
        detailQueryKey: "naturalPerson",
        send: sendNaturalPersonToAuthority,
        assign: assignNaturalPersonAuthorityId,
        merge: mergeNaturalPersonInto,
        mergeDuplicates: mergeNaturalPersonDuplicates,
        fetchMergeCandidates: fetchNaturalPersonMergeCandidates,
        proposeMerge: proposeNaturalPersonMerge,
        fetchById: fetchNaturalPersonById,
    },
    legal: {
        legalForm: PersonLegalFormEnum.LEGAL,
        listQueryKey: "legal-persons",
        detailQueryKey: "legalPerson",
        send: sendLegalPersonToAuthority,
        assign: assignLegalPersonAuthorityId,
        merge: mergeLegalPersonInto,
        mergeDuplicates: mergeLegalPersonDuplicates,
        fetchMergeCandidates: fetchLegalPersonMergeCandidates,
        proposeMerge: proposeLegalPersonMerge,
        fetchById: fetchLegalPersonById,
    },
};
var ERROR_MESSAGE_IDS = (_a = {},
    _a[ErrorType.PERSON_AUTHORITY_LINK_REQUIRES_AGENCY_WORKER] = "ui-br-agency.person.authorityLink.error.forbidden",
    _a[ErrorType.NATURAL_PERSON_BIOGRAPHICAL_NOTE_REQUIRED] = "ui-br-agency.person.authorityLink.error.biographicalNoteRequired",
    _a[ErrorType.PERSON_ALREADY_LINKED_TO_AUTHORITY] = "ui-br-agency.person.authorityLink.error.alreadyLinked",
    _a[ErrorType.PERSON_AUTHORITY_ID_DUPLICATE] = "ui-br-agency.person.authorityLink.error.duplicateId",
    _a[ErrorType.PERSON_AUTHORITY_ID_REQUIRED] = "ui-br-agency.person.authorityLink.error.idRequired",
    _a[ErrorType.PERSON_MERGE_INVALID_TARGET] = "ui-br-agency.person.authorityLink.error.invalidMergeTarget",
    _a[ErrorType.PERSON_ALREADY_SENT_TO_AUTHORITY] = "ui-br-agency.person.authorityLink.error.alreadySent",
    _a);
/** Messages of a failed "Sloučit duplicity": they speak about the chosen duplicate. */
var DUPLICATES_ERROR_MESSAGE_IDS = (_b = {},
    _b[ErrorType.PERSON_AUTHORITY_LINK_REQUIRES_AGENCY_WORKER] = "ui-br-agency.person.authorityLink.error.forbidden",
    _b[ErrorType.PERSON_ALREADY_LINKED_TO_AUTHORITY] = "ui-br-agency.person.authorityLink.duplicates.error.linked",
    _b[ErrorType.PERSON_ALREADY_SENT_TO_AUTHORITY] = "ui-br-agency.person.authorityLink.duplicates.error.alreadySent",
    _b[ErrorType.PERSON_MERGE_INVALID_SOURCE] = "ui-br-agency.person.authorityLink.duplicates.error.invalidSource",
    _b);
/** Messages of a failed "Navrhnout sloučení" (DB-756). */
var PROPOSE_ERROR_MESSAGE_IDS = (_c = {},
    _c[ErrorType.PERSON_AUTHORITY_LINK_REQUIRES_AGENCY_WORKER] = "ui-br-agency.person.authorityLink.error.forbidden",
    _c[ErrorType.PERSON_MERGE_INVALID_TARGET] = "ui-br-agency.person.authorityLink.proposeMerge.error.invalidTarget",
    _c[ErrorType.PERSON_MERGE_PROPOSAL_NOT_LINKED_TO_AUTHORITY] = "ui-br-agency.person.authorityLink.proposeMerge.error.notLinked",
    _c);
/** react-query key prefix of the duplicate candidates of a person. */
export var PERSON_MERGE_CANDIDATES_QUERY_KEY = "personMergeCandidates";
/**
 * Actions linking a local-only (unlinked) person to NK authorities (DB-635):
 * send it to NK authorities, assign an existing NK identifier, or merge it into
 * an existing person. Offered only to agency workers / superusers and only for
 * a person without an NK identifier. Plus (DB-653) merging duplicates without
 * an NK identifier into the opened person, which may be any person, and
 * (DB-756) proposing to the NK authority department by e-mail that a person
 * linked to an NK authority be merged into another such person (nothing
 * changes in ReČeK).
 */
export var usePersonAuthorityLink = function (_a) {
    var _b, _c;
    var kind = _a.kind, person = _a.person, onUpdated = _a.onUpdated, onMerged = _a.onMerged;
    var config = KIND_CONFIG[kind];
    var ky = useOkapiKy();
    var callout = useCallout();
    var queryClient = useQueryClient();
    var formatMessage = useIntl().formatMessage;
    var canManagePersonAuthorityLink = usePermissions().canManagePersonAuthorityLink;
    var notifySendError = useAuthoritySendErrorCallout().notifySendError;
    var _d = useState(false), isBusy = _d[0], setIsBusy = _d[1];
    var _e = useState(false), assignOpen = _e[0], setAssignOpen = _e[1];
    var _f = useState(null), assignError = _f[0], setAssignError = _f[1];
    var _g = useState(null), conflictingPersonId = _g[0], setConflictingPersonId = _g[1];
    var _h = useState(false), sendNoteOpen = _h[0], setSendNoteOpen = _h[1];
    var _j = useState(null), sendNoteError = _j[0], setSendNoteError = _j[1];
    var _k = useState(false), mergeOpen = _k[0], setMergeOpen = _k[1];
    var _l = useState(null), mergeError = _l[0], setMergeError = _l[1];
    var _m = useState(null), mergeTarget = _m[0], setMergeTarget = _m[1];
    var _o = useState(false), proposeOpen = _o[0], setProposeOpen = _o[1];
    var _p = useState(null), proposeError = _p[0], setProposeError = _p[1];
    var _q = useState(false), duplicatesOpen = _q[0], setDuplicatesOpen = _q[1];
    var _r = useState(null), duplicatesError = _r[0], setDuplicatesError = _r[1];
    var _s = useState(null), duplicatesErrorPersonId = _s[0], setDuplicatesErrorPersonId = _s[1];
    var canManage = !!(person === null || person === void 0 ? void 0 : person.id) && !!canManagePersonAuthorityLink && isPersonUnlinked(person);
    var canSend = canManage && canSendPerson(person);
    var canMerge = canManage && canMergePerson(person);
    // Any person may take duplicates over, linked to NK or not (DB-653).
    var canMergeDuplicates = !!(person === null || person === void 0 ? void 0 : person.id) && !!canManagePersonAuthorityLink;
    // Two NK authorities can only be merged by the NK authority department (DB-756).
    var canProposeMerge = !!(person === null || person === void 0 ? void 0 : person.id) && !!canManagePersonAuthorityLink && !isPersonUnlinked(person);
    var candidatesQuery = useQuery([PERSON_MERGE_CANDIDATES_QUERY_KEY, kind, person === null || person === void 0 ? void 0 : person.id], function () { return config.fetchMergeCandidates(ky, person.id); }, { enabled: duplicatesOpen && canMergeDuplicates });
    var needsBiographicalNote = kind === "natural" &&
        !((_b = person === null || person === void 0 ? void 0 : person.biographicalNote) === null || _b === void 0 ? void 0 : _b.trim());
    var errorMessage = useCallback(function (code, fallbackId) {
        return formatMessage({
            id: (code && ERROR_MESSAGE_IDS[code]) || fallbackId,
        });
    }, [formatMessage]);
    var notifySuccess = useCallback(function (messageId) {
        return callout === null || callout === void 0 ? void 0 : callout.sendCallout({
            type: "success",
            message: formatMessage({ id: messageId }),
        });
    }, [callout, formatMessage]);
    var refreshAfterUpdate = function (updated) {
        queryClient.invalidateQueries(config.listQueryKey);
        queryClient.invalidateQueries(["personSearchOptions"]);
        if (updated.id) {
            queryClient.setQueryData([config.detailQueryKey, updated.id], updated);
        }
        onUpdated(updated);
    };
    /**
     * Sends the person; the optional note is stored on a natural person first
     * (AC9). Returns whether the request succeeded.
     */
    var sendPerson = function (id, biographicalNote) { return __awaiter(void 0, void 0, void 0, function () {
        var updated, _a, error_1, code;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setIsBusy(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 6, 8, 9]);
                    if (!biographicalNote) return [3 /*break*/, 3];
                    return [4 /*yield*/, config.send(ky, id, biographicalNote)];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 5];
                case 3: return [4 /*yield*/, config.send(ky, id)];
                case 4:
                    _a = _b.sent();
                    _b.label = 5;
                case 5:
                    updated = _a;
                    refreshAfterUpdate(updated);
                    if (!notifySendError(updated)) {
                        notifySuccess("ui-br-agency.person.authorityLink.send.success");
                    }
                    return [2 /*return*/, { ok: true }];
                case 6:
                    error_1 = _b.sent();
                    return [4 /*yield*/, parsePersonAuthorityLinkError(error_1)];
                case 7:
                    code = (_b.sent()).code;
                    return [2 /*return*/, { ok: false, code: code }];
                case 8:
                    setIsBusy(false);
                    return [7 /*endfinally*/];
                case 9: return [2 /*return*/];
            }
        });
    }); };
    /**
     * "Odeslat do NA": a natural person without a biographical note (required by
     * Aleph) first gets a dialog asking for it; everyone else is sent at once.
     */
    var sendToAuthority = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a, ok, code;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!(person === null || person === void 0 ? void 0 : person.id))
                        return [2 /*return*/];
                    if (needsBiographicalNote) {
                        setSendNoteError(null);
                        setSendNoteOpen(true);
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, sendPerson(person.id)];
                case 1:
                    _a = _b.sent(), ok = _a.ok, code = _a.code;
                    if (!ok) {
                        callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                            type: "error",
                            timeout: 0,
                            message: errorMessage(code, "ui-br-agency.person.authorityLink.send.error"),
                        });
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    var submitSendWithNote = function (biographicalNote) { return __awaiter(void 0, void 0, void 0, function () {
        var trimmed, _a, ok, code;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!(person === null || person === void 0 ? void 0 : person.id))
                        return [2 /*return*/];
                    trimmed = biographicalNote.trim();
                    if (!trimmed) {
                        setSendNoteError(errorMessage(ErrorType.NATURAL_PERSON_BIOGRAPHICAL_NOTE_REQUIRED, "ui-br-agency.person.authorityLink.send.error"));
                        return [2 /*return*/];
                    }
                    setSendNoteError(null);
                    return [4 /*yield*/, sendPerson(person.id, trimmed)];
                case 1:
                    _a = _b.sent(), ok = _a.ok, code = _a.code;
                    if (ok) {
                        setSendNoteOpen(false);
                    }
                    else {
                        setSendNoteError(errorMessage(code, "ui-br-agency.person.authorityLink.send.error"));
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    var openAssign = function () {
        setAssignError(null);
        setConflictingPersonId(null);
        setAssignOpen(true);
    };
    var closeAssign = function () { return setAssignOpen(false); };
    var assignAuthorityId = function (authorityId) { return __awaiter(void 0, void 0, void 0, function () {
        var trimmed, updated, error_2, parsed;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(person === null || person === void 0 ? void 0 : person.id))
                        return [2 /*return*/];
                    trimmed = authorityId.trim();
                    setAssignError(null);
                    setConflictingPersonId(null);
                    if (!trimmed) {
                        setAssignError(errorMessage(ErrorType.PERSON_AUTHORITY_ID_REQUIRED, "ui-br-agency.person.authorityLink.assign.error"));
                        return [2 /*return*/];
                    }
                    setIsBusy(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 5, 6]);
                    return [4 /*yield*/, config.assign(ky, person.id, trimmed)];
                case 2:
                    updated = _a.sent();
                    setAssignOpen(false);
                    refreshAfterUpdate(updated);
                    notifySuccess("ui-br-agency.person.authorityLink.assign.success");
                    return [3 /*break*/, 6];
                case 3:
                    error_2 = _a.sent();
                    return [4 /*yield*/, parsePersonAuthorityLinkError(error_2)];
                case 4:
                    parsed = _a.sent();
                    setAssignError(errorMessage(parsed.code, "ui-br-agency.person.authorityLink.assign.error"));
                    if (parsed.code === ErrorType.PERSON_AUTHORITY_ID_DUPLICATE &&
                        parsed.conflictingPersonId &&
                        parsed.conflictingPersonId !== person.id) {
                        setConflictingPersonId(parsed.conflictingPersonId);
                    }
                    return [3 /*break*/, 6];
                case 5:
                    setIsBusy(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    var openMerge = function (targetId) { return __awaiter(void 0, void 0, void 0, function () {
        var target_1, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setMergeError(null);
                    setMergeTarget(targetId ? { id: targetId } : null);
                    setAssignOpen(false);
                    setMergeOpen(true);
                    if (!targetId)
                        return [2 /*return*/];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, config.fetchById(ky, targetId)];
                case 2:
                    target_1 = _b.sent();
                    setMergeTarget(function (current) {
                        return (current === null || current === void 0 ? void 0 : current.id) === targetId ? { id: targetId, person: target_1 } : current;
                    });
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    // Without the holder there is no label to show: let the user pick the
                    // target in the search instead (the merge is validated by the backend).
                    setMergeTarget(function (current) { return ((current === null || current === void 0 ? void 0 : current.id) === targetId ? null : current); });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    /** Offers merging into the person already holding the duplicate NK identifier. */
    var mergeIntoConflictingPerson = function () {
        if (conflictingPersonId) {
            openMerge(conflictingPersonId);
        }
    };
    var closeMerge = function () { return setMergeOpen(false); };
    var mergeInto = function (targetId) { return __awaiter(void 0, void 0, void 0, function () {
        var target, error_3, code;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(person === null || person === void 0 ? void 0 : person.id))
                        return [2 /*return*/];
                    setMergeError(null);
                    setIsBusy(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 5, 6]);
                    return [4 /*yield*/, config.merge(ky, person.id, targetId)];
                case 2:
                    target = _a.sent();
                    setMergeOpen(false);
                    queryClient.invalidateQueries(config.listQueryKey);
                    queryClient.invalidateQueries(["personSearchOptions"]);
                    queryClient.removeQueries([config.detailQueryKey, person.id]);
                    // The source's title contributions moved to the target.
                    queryClient.invalidateQueries([PERSON_TITLES_QUERY_KEY]);
                    if (target === null || target === void 0 ? void 0 : target.id) {
                        queryClient.setQueryData([config.detailQueryKey, target.id], target);
                    }
                    notifySuccess("ui-br-agency.person.authorityLink.merge.success");
                    onMerged(target);
                    return [3 /*break*/, 6];
                case 3:
                    error_3 = _a.sent();
                    return [4 /*yield*/, parsePersonAuthorityLinkError(error_3)];
                case 4:
                    code = (_a.sent()).code;
                    setMergeError(errorMessage(code, "ui-br-agency.person.authorityLink.merge.error"));
                    return [3 /*break*/, 6];
                case 5:
                    setIsBusy(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    var openPropose = function () {
        setProposeError(null);
        setProposeOpen(true);
    };
    var closePropose = function () { return setProposeOpen(false); };
    /**
     * E-mails the proposal that the opened person (disappears) be merged into
     * the target (stays) to the NK authority department (DB-756). Nothing
     * changes locally, so no query is refreshed; a failure stays in the dialog.
     */
    var proposeMerge = function (targetId, note) { return __awaiter(void 0, void 0, void 0, function () {
        var error_4, code;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(person === null || person === void 0 ? void 0 : person.id) || !targetId)
                        return [2 /*return*/];
                    setProposeError(null);
                    setIsBusy(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 5, 6]);
                    return [4 /*yield*/, config.proposeMerge(ky, person.id, targetId, note)];
                case 2:
                    _a.sent();
                    setProposeOpen(false);
                    notifySuccess("ui-br-agency.person.authorityLink.proposeMerge.success");
                    return [3 /*break*/, 6];
                case 3:
                    error_4 = _a.sent();
                    return [4 /*yield*/, parsePersonAuthorityLinkError(error_4)];
                case 4:
                    code = (_a.sent()).code;
                    setProposeError(formatMessage({
                        id: (code && PROPOSE_ERROR_MESSAGE_IDS[code]) ||
                            "ui-br-agency.person.authorityLink.proposeMerge.error",
                    }));
                    return [3 /*break*/, 6];
                case 5:
                    setIsBusy(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    var openDuplicates = function () {
        setDuplicatesError(null);
        setDuplicatesErrorPersonId(null);
        setDuplicatesOpen(true);
    };
    var closeDuplicates = function () { return setDuplicatesOpen(false); };
    /**
     * Merges the chosen duplicates into the opened person, which stays open and
     * is refreshed; the duplicates disappear from the list (DB-653).
     */
    var mergeDuplicates = function (sourceIds) { return __awaiter(void 0, void 0, void 0, function () {
        var survivor, error_5, parsed;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!(person === null || person === void 0 ? void 0 : person.id) || sourceIds.length === 0)
                        return [2 /*return*/];
                    setDuplicatesError(null);
                    setDuplicatesErrorPersonId(null);
                    setIsBusy(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 5, 6]);
                    return [4 /*yield*/, config.mergeDuplicates(ky, person.id, sourceIds)];
                case 2:
                    survivor = _b.sent();
                    setDuplicatesOpen(false);
                    queryClient.invalidateQueries(config.listQueryKey);
                    queryClient.invalidateQueries(["personSearchOptions"]);
                    queryClient.invalidateQueries([PERSON_MERGE_CANDIDATES_QUERY_KEY]);
                    sourceIds.forEach(function (id) {
                        return queryClient.removeQueries([config.detailQueryKey, id]);
                    });
                    // The duplicates' title contributions moved to the opened person.
                    queryClient.invalidateQueries([PERSON_TITLES_QUERY_KEY]);
                    if (survivor === null || survivor === void 0 ? void 0 : survivor.id) {
                        queryClient.setQueryData([config.detailQueryKey, survivor.id], survivor);
                    }
                    notifySuccess("ui-br-agency.person.authorityLink.duplicates.success");
                    onUpdated(survivor);
                    return [3 /*break*/, 6];
                case 3:
                    error_5 = _b.sent();
                    return [4 /*yield*/, parsePersonAuthorityLinkError(error_5)];
                case 4:
                    parsed = _b.sent();
                    setDuplicatesError(formatMessage({
                        id: (parsed.code && DUPLICATES_ERROR_MESSAGE_IDS[parsed.code]) ||
                            "ui-br-agency.person.authorityLink.duplicates.error",
                    }));
                    setDuplicatesErrorPersonId((_a = parsed.personId) !== null && _a !== void 0 ? _a : null);
                    return [3 /*break*/, 6];
                case 5:
                    setIsBusy(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    return {
        kind: kind,
        legalForm: config.legalForm,
        person: person,
        canManage: canManage,
        canSend: canSend,
        canMerge: canMerge,
        canMergeDuplicates: canMergeDuplicates,
        canProposeMerge: canProposeMerge,
        isBusy: isBusy,
        sendToAuthority: sendToAuthority,
        send: {
            isOpen: sendNoteOpen,
            error: sendNoteError,
            close: function () { return setSendNoteOpen(false); },
            submit: submitSendWithNote,
        },
        assign: {
            isOpen: assignOpen,
            error: assignError,
            conflictingPersonId: conflictingPersonId,
            open: openAssign,
            close: closeAssign,
            submit: assignAuthorityId,
            mergeIntoConflictingPerson: mergeIntoConflictingPerson,
        },
        merge: {
            isOpen: mergeOpen,
            error: mergeError,
            target: mergeTarget,
            setTarget: setMergeTarget,
            open: function () { return openMerge(); },
            close: closeMerge,
            submit: mergeInto,
        },
        propose: {
            isOpen: proposeOpen,
            error: proposeError,
            open: openPropose,
            close: closePropose,
            submit: proposeMerge,
        },
        duplicates: {
            isOpen: duplicatesOpen,
            error: duplicatesError,
            errorPersonId: duplicatesErrorPersonId,
            candidates: ((_c = candidatesQuery.data) !== null && _c !== void 0 ? _c : []),
            candidatesLoading: candidatesQuery.isLoading,
            candidatesError: candidatesQuery.isError,
            open: openDuplicates,
            close: closeDuplicates,
            submit: mergeDuplicates,
        },
    };
};
